import { useBookingPaymentAccounts } from '@/api/bookings';
import { AccountChoice } from '@/components/AccountChoice';
import { useState } from 'react';

import type { Booking, PaymentMethod } from '@/api/bookings';
import { Button, Card, Notice } from '@/components/PageHeading';
import { depositPaidBy, everydayMethods, refundMethods } from '@core/bookings/payment';
import { paymentMethodLabel } from '@core/bookings/paymentWords';
import { forfeitedAmount, settleBooking, settleLabel } from '@core/bookings/settlement';
import { formatAmount } from '@core/format/money';

import { CounterBand } from './CounterBand';
import { Choice, Group } from './Dialog';
import { MoneyLine, MoneyLines } from './MoneyLine';

/**
 * Closing a deposit the shop is holding — a page, the phone's `app/booking/settle.tsx` on the
 * website (2026-10-22); it was a pop-over on a button. Held, each deduction on its own line, and
 * <strong>"Back to the customer" as the large figure</strong>, with how it goes back as choices that
 * start on the way the deposit came in (`depositPaidBy`).
 *
 * <p>
 * <strong>This calls settlement, never the payment endpoint</strong>: a `DepositRefund` payment moves
 * money and leaves the deposit open; settlement takes the deductions, writes the refund row and the
 * closing state. The deposit pays what is still owed before anything goes back (owner, 2026-10-14);
 * where it does not cover it all, the same press takes the rest.
 * </p>
 */
export function SettlePage({
  booking,
  currency,
  busy,
  onSettle,
  onBack,
}: {
  booking: Booking;
  currency: string;
  busy: boolean;
  /**
   * The method, whether the same press takes what the deposit does not cover, and the account the
   * money moves through — the refund leaves it, or what is taken goes into it (null: the default).
   */
  onSettle: (method: PaymentMethod, collect: boolean, cashAccountId: number | null) => void;
  onBack: () => void;
}) {
  const cash = (amount: number) => `${currency}${formatAmount(amount)}`;
  const [method, setMethod] = useState<PaymentMethod>(() => depositPaidBy(booking));
  // Which account the refund leaves, or what is taken goes into (split payments); null is the
  // method's default.
  const [account, setAccount] = useState<number | null>(null);
  const accounts = useBookingPaymentAccounts(booking.branchId ?? undefined);
  const settlement = settleBooking(booking);
  const collecting = settlement.toCollect > 0;

  return (
    <>
      <CounterBand
        eyebrow={`${booking.documentNumber} · ${booking.customerName}`}
        title="Settle the deposit"
        onBack={onBack}
      />

      <div className="mx-auto w-full max-w-[560px]">
        {booking.deposit.state !== 'Held' ? (
          <Card>
            <Notice tone="warning" title="There is no deposit held on this booking to settle." />
            <div className="mt-3">
              <Button onClick={onBack}>Back to the booking</Button>
            </div>
          </Card>
        ) : (
          <Card>
            <MoneyLines>
              <MoneyLine label="Held" value={cash(settlement.depositHeld)} tone="brand" />
              {settlement.deductions.map((deduction, index) => (
                <MoneyLine
                  key={`${deduction.label}-${index}`}
                  label={deduction.label}
                  hint={deduction.wearerName}
                  value={`− ${cash(deduction.amount)}`}
                  tone="danger"
                />
              ))}
              {settlement.towardsBalance > 0 && (
                <MoneyLine
                  label="Rent still owed"
                  hint="Paid from the deposit"
                  value={`− ${cash(settlement.towardsBalance)}`}
                  tone="warning"
                />
              )}
            </MoneyLines>

            <div className="border-border-color my-4 border-t pt-4 text-center">
              <p className="text-default text-[12.5px]">Back to the customer</p>
              <p className="font-mono-nums text-success text-3xl font-bold">
                {cash(settlement.refund)}
              </p>
              {forfeitedAmount(settlement) > 0 && (
                <p className="text-default mt-1 text-[12px]">
                  Charges include GST — {cash(forfeitedAmount(settlement))} kept is taxed on the
                  settlement note. Rent paid from the deposit is not taxed again.
                </p>
              )}
            </div>

            {collecting && (
              <div className="mb-3">
                <Notice
                  tone="warning"
                  title={`The deposit does not cover it all — ${cash(settlement.toCollect)} more to take.`}
                />
              </div>
            )}

            <Group label={collecting ? 'How they pay the rest' : 'How it goes back'}>
              <div className="flex flex-wrap gap-1.5">
                {(collecting ? everydayMethods : refundMethods).map((option) => (
                  <Choice
                    key={option}
                    label={paymentMethodLabel[option]}
                    chosen={method === option}
                    disabled={busy}
                    onChoose={() => {
                      setMethod(option);
                      setAccount(null);
                    }}
                  />
                ))}
              </div>
            </Group>

            {(collecting || settlement.refund > 0) && (
              <div className="mt-3">
                <AccountChoice
                  label={collecting ? 'Into which account?' : 'Out of which account?'}
                  method={method}
                  accounts={accounts.data}
                  value={account}
                  onChange={setAccount}
                  disabled={busy}
                />
              </div>
            )}

            <Button
              variant="primary"
              shortcut="save"
              disabled={busy}
              onClick={() => onSettle(method, collecting, account)}
              className="mt-4 h-12 w-full justify-center text-[15px]"
            >
              {settleLabel(settlement, paymentMethodLabel[method], cash)}
            </Button>
          </Card>
        )}
      </div>
    </>
  );
}
